import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const CookiePolicy = () => {
  const [activeSection, setActiveSection] = useState(null);

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const cookieCategories = [
    {
      id: 'essential',
      name: 'Strictly Necessary Cookies',
      description: 'These cookies are essential for the website to function properly and cannot be disabled.',
      examples: [
        'Session cookies for maintaining login state',
        'Security cookies for fraud prevention',
        'Load balancing cookies for optimal performance'
      ],
      duration: 'Session or up to 24 hours',
      canDisable: false
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      description: 'These cookies enable enhanced functionality and personalization.',
      examples: [
        'Language preference cookies',
        'Region selection cookies',
        'User interface customization'
      ],
      duration: 'Up to 1 year',
      canDisable: true
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website.',
      examples: [
        'Google Analytics for usage statistics',
        'Page view tracking',
        'User journey analysis'
      ],
      duration: 'Up to 2 years',
      canDisable: true
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'These cookies are used to track visitors across websites for marketing purposes.',
      examples: [
        'Advertising cookies',
        'Retargeting cookies',
        'Social media integration cookies'
      ],
      duration: 'Up to 2 years',
      canDisable: true
    }
  ];

  const thirdPartyCookies = [
    { name: 'Google Analytics', purpose: 'Website analytics and performance tracking', link: 'https://policies.google.com/privacy' },
    { name: 'YouTube', purpose: 'Video content embedding', link: 'https://policies.google.com/privacy' },
    { name: 'LinkedIn', purpose: 'Social media integration and professional networking', link: 'https://www.linkedin.com/legal/privacy-policy' },
    { name: 'Twitter/X', purpose: 'Social media integration and content sharing', link: 'https://twitter.com/privacy' }
  ];

  return (
    <>
      <SEO 
        title="Cookie Policy - How We Use Cookies"
        description="Learn about how Abacies Logiciels uses cookies and similar technologies to improve your browsing experience, analyze site traffic, and personalize content."
        keywords="cookie policy, cookies, tracking, privacy, data collection, website analytics, user preferences"
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blush-light/40 rounded-2xl mb-6">
              <svg className="w-10 h-10 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              Cookie <span className="text-gradient">Policy</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed animate-slide-up mb-4">
              Understanding how we use cookies to enhance your experience
            </p>
            <p className="text-sm text-cool-grey">
              Last Updated: December 1, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="py-12 bg-white border-b border-platinum">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="bg-blush-light/20 rounded-2xl p-8 border-l-4 border-primary-red">
              <h2 className="text-2xl font-bold mb-4 text-charcoal">Quick Summary</h2>
              <p className="text-titanium mb-4">
                Abacies Logiciels uses cookies and similar tracking technologies to improve your browsing experience, analyze website traffic, and provide personalized content. This policy explains what cookies are, how we use them, and how you can control them.
              </p>
              <ul className="list-disc list-inside space-y-2 text-titanium">
                <li>We use both essential and non-essential cookies</li>
                <li>You can control non-essential cookies through your browser settings</li>
                <li>Some cookies are set by third-party services we use</li>
                <li>We comply with GDPR, CCPA, and other privacy regulations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-12 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <h2 className="text-2xl font-bold mb-6 text-charcoal">Table of Contents</h2>
              <nav className="grid md:grid-cols-2 gap-4">
                {[
                  { title: 'What Are Cookies?', id: 'what-are-cookies' },
                  { title: 'Types of Cookies We Use', id: 'cookie-types' },
                  { title: 'Third-Party Cookies', id: 'third-party' },
                  { title: 'How to Control Cookies', id: 'cookie-control' },
                  { title: 'Cookie Duration', id: 'cookie-duration' },
                  { title: 'Updates to This Policy', id: 'policy-updates' }
                ].map((item, index) => (
                  <a
                    key={index}
                    href={`#${item.id}`}
                    className="flex items-center p-3 rounded-lg hover:bg-blush-light/30 transition-colors duration-300 text-graphite hover:text-primary-red"
                  >
                    <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                    {item.title}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto space-y-12">
            
            {/* What Are Cookies */}
            <div id="what-are-cookies" className="scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 text-charcoal">What Are Cookies?</h2>
              <div className="prose max-w-none">
                <p className="text-titanium mb-4">
                  Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
                </p>
                <p className="text-titanium mb-4">
                  Cookies can be "session cookies" (which are deleted when you close your browser) or "persistent cookies" (which remain on your device for a set period or until you delete them).
                </p>
                <div className="bg-alabaster rounded-lg p-6 mt-6">
                  <h3 className="text-xl font-semibold mb-3 text-charcoal">Similar Technologies</h3>
                  <p className="text-titanium mb-4">
                    In addition to cookies, we may use similar technologies including:
                  </p>
                  <ul className="space-y-2 text-titanium">
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>Web beacons:</strong> Small graphic images embedded in web pages or emails</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>Pixels:</strong> Code snippets that allow us to measure user engagement</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>Local storage:</strong> Data stored in your browser for enhanced functionality</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Types of Cookies */}
            <div id="cookie-types" className="scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 text-charcoal">Types of Cookies We Use</h2>
              <p className="text-titanium mb-6">
                We use different types of cookies to provide, maintain, and improve our services. Below is a detailed breakdown of each category:
              </p>
              <div className="space-y-4">
                {cookieCategories.map((category) => (
                  <div key={category.id} className="card">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleSection(category.id)}
                    >
                      <div className="flex items-center flex-1">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${
                          category.canDisable ? 'bg-blush-light/40' : 'bg-primary-red/10'
                        }`}>
                          {category.canDisable ? (
                            <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-charcoal">{category.name}</h3>
                          <p className="text-sm text-titanium mt-1">{category.description}</p>
                        </div>
                      </div>
                      <svg 
                        className={`w-6 h-6 text-titanium transition-transform duration-300 ml-4 ${
                          activeSection === category.id ? 'transform rotate-180' : ''
                        }`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    
                    {activeSection === category.id && (
                      <div className="mt-6 pt-6 border-t border-platinum animate-slide-down">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-semibold text-charcoal mb-3">Examples:</h4>
                            <ul className="space-y-2">
                              {category.examples.map((example, idx) => (
                                <li key={idx} className="flex items-start text-sm text-titanium">
                                  <svg className="w-4 h-4 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                  {example}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-semibold text-charcoal mb-3">Duration:</h4>
                            <p className="text-sm text-titanium mb-4">{category.duration}</p>
                            <h4 className="font-semibold text-charcoal mb-3">Can be disabled:</h4>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                              category.canDisable 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {category.canDisable ? 'Yes' : 'No - Required for website function'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Third-Party Cookies */}
            <div id="third-party" className="scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 text-charcoal">Third-Party Cookies</h2>
              <p className="text-titanium mb-6">
                We use services from trusted third-party providers who may set their own cookies when you visit our website. We do not control these cookies and recommend reviewing each provider's privacy policy.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {thirdPartyCookies.map((provider, index) => (
                  <div key={index} className="card bg-alabaster">
                    <h3 className="font-semibold text-charcoal mb-2">{provider.name}</h3>
                    <p className="text-sm text-titanium mb-3">{provider.purpose}</p>
                    <a 
                      href={provider.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-red hover:underline text-sm inline-flex items-center"
                    >
                      View Privacy Policy
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                ))}
              </div>
            </div>

            {/* How to Control Cookies */}
            <div id="cookie-control" className="scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 text-charcoal">How to Control Cookies</h2>
              <p className="text-titanium mb-6">
                You have several options to manage or delete cookies:
              </p>
              
              <div className="space-y-6">
                {/* Browser Settings */}
                <div className="card bg-alabaster">
                  <h3 className="text-xl font-semibold mb-4 text-charcoal flex items-center">
                    <svg className="w-6 h-6 text-primary-red mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    Browser Settings
                  </h3>
                  <p className="text-titanium mb-4">
                    Most browsers allow you to control cookies through their settings. Here's how to manage cookies in popular browsers:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { name: 'Google Chrome', link: 'https://support.google.com/chrome/answer/95647' },
                      { name: 'Mozilla Firefox', link: 'https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer' },
                      { name: 'Safari', link: 'https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac' },
                      { name: 'Microsoft Edge', link: 'https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09' }
                    ].map((browser, idx) => (
                      <a
                        key={idx}
                        href={browser.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 bg-white rounded-lg hover:shadow-md transition-all duration-300"
                      >
                        <span className="text-graphite">{browser.name}</span>
                        <svg className="w-5 h-5 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Cookie Preference Center */}
                <div className="card border-2 border-primary-red">
                  <h3 className="text-xl font-semibold mb-4 text-charcoal flex items-center">
                    <svg className="w-6 h-6 text-primary-red mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    Cookie Preference Center
                  </h3>
                  <p className="text-titanium mb-4">
                    Manage your cookie preferences directly on our website:
                  </p>
                  <button className="btn-primary">
                    Manage Cookie Preferences
                  </button>
                </div>

                {/* Important Note */}
                <div className="bg-blush-light/20 rounded-lg p-6 border-l-4 border-coral-mist">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-coral-mist mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h4 className="font-semibold text-charcoal mb-2">Important Note</h4>
                      <p className="text-titanium text-sm">
                        Disabling certain cookies may affect the functionality of our website and limit your access to certain features. Strictly necessary cookies cannot be disabled as they are essential for the website to function.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cookie Duration */}
            <div id="cookie-duration" className="scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 text-charcoal">Cookie Duration</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-semibold mb-3 text-charcoal flex items-center">
                    <svg className="w-5 h-5 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Session Cookies
                  </h3>
                  <p className="text-titanium text-sm">
                    These cookies are temporary and are deleted when you close your browser. They help maintain your session and ensure security during your visit.
                  </p>
                </div>
                <div className="card">
                  <h3 className="text-lg font-semibold mb-3 text-charcoal flex items-center">
                    <svg className="w-5 h-5 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Persistent Cookies
                  </h3>
                  <p className="text-titanium text-sm">
                    These cookies remain on your device for a set period or until you delete them. They help remember your preferences and provide personalized experiences across visits.
                  </p>
                </div>
              </div>
            </div>

            {/* Updates to Policy */}
            <div id="policy-updates" className="scroll-mt-24">
              <h2 className="text-3xl font-bold mb-6 text-charcoal">Updates to This Cookie Policy</h2>
              <p className="text-titanium mb-4">
                We may update this Cookie Policy from time to time to reflect changes in technology, legislation, our operations, or for other operational, legal, or regulatory reasons.
              </p>
              <p className="text-titanium mb-4">
                When we make significant changes, we will notify you by:
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  'Posting the updated policy on this page',
                  'Updating the "Last Updated" date at the top of this policy',
                  'Sending an email notification to registered users',
                  'Displaying a prominent notice on our website'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start text-titanium">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-titanium">
                We encourage you to review this Cookie Policy periodically to stay informed about how we use cookies.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="card border-2 border-primary-red">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-4 text-charcoal">Questions About Cookies?</h2>
                <p className="text-titanium mb-6">
                  If you have questions about our use of cookies or this Cookie Policy, please contact us:
                </p>
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div className="flex flex-col items-center">
                    <svg className="w-8 h-8 text-primary-red mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm font-semibold text-charcoal">Email</p>
                    <a href="mailto:privacy@abacies.com" className="text-primary-red hover:underline text-sm">
                      privacy@abacies.com
                    </a>
                  </div>
                  <div className="flex flex-col items-center">
                    <svg className="w-8 h-8 text-primary-red mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <p className="text-sm font-semibold text-charcoal">Phone</p>
                    <p className="text-titanium text-sm">+1 (555) 123-4567</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <svg className="w-8 h-8 text-primary-red mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-sm font-semibold text-charcoal">Address</p>
                    <p className="text-titanium text-sm text-center">123 Technology Drive<br />Silicon Valley, CA 94025</p>
                  </div>
                </div>
                <Link to="/contact" className="btn-primary inline-block">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Policies */}
      <section className="py-16 bg-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-charcoal text-center">Related Policies</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/legal/privacy" className="card hover:shadow-lg transition-all duration-300 text-center">
                <svg className="w-12 h-12 text-primary-red mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <h3 className="font-semibold text-charcoal mb-2">Privacy Policy</h3>
                <p className="text-sm text-titanium">Learn how we protect your data</p>
              </Link>
              <Link to="/legal/terms" className="card hover:shadow-lg transition-all duration-300 text-center">
                <svg className="w-12 h-12 text-primary-red mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="font-semibold text-charcoal mb-2">Terms of Service</h3>
                <p className="text-sm text-titanium">Review our terms and conditions</p>
              </Link>
              <Link to="/legal/gdpr" className="card hover:shadow-lg transition-all duration-300 text-center">
                <svg className="w-12 h-12 text-primary-red mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <h3 className="font-semibold text-charcoal mb-2">GDPR Compliance</h3>
                <p className="text-sm text-titanium">Our commitment to data protection</p>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CookiePolicy;